import React from 'react';
import { Helmet } from 'react-helmet-async';
import ArtifactSEO from './ArtifactSEO';
import seoConfig from '../../config/seo.json';

const SEO = ({ title, description, pathname = '', agentKey = null }) => {
  const { default: defaultSEO, pages } = seoConfig;

  const seo = {
    title: title || defaultSEO.title,
    description: description || defaultSEO.description,
    url: `${defaultSEO.url}${pathname}`,
    image: defaultSEO.image,
  };

  // Generate SoftwareApplication + BreadcrumbList schemas for agent pages
  const schemas = [];
  let artifactData = null;

  if (agentKey && pages[agentKey]) {
    const agent = pages[agentKey];

    // Prepare Artifact Data for AEO
    artifactData = {
      agentName: agent.title.replace(' | Eagentix', ''),
      capabilities: agent.faqs?.map(f => f.question) || [],
      deliverables: {
        "Format": "Autonomous Agent Artifact",
        "Workflow": agent.howTo?.steps?.join('; ') || "Production Delivery"
      },
      governance: [
        "SOC2 Compliance",
        "Policy-as-Code Guardrails",
        "Model-Agnostic Reasoning"
      ]
    };

    // SoftwareApplication schema
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: agent.title.replace(' | Eagentix', ''),
      description: agent.description,
      url: seo.url,
      applicationCategory: 'BusinessApplication',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock'
      },
      author: {
        '@type': 'Organization',
        name: 'Eagentix',
        url: defaultSEO.url,
        logo: `${defaultSEO.url}/logo512.png`
      }
    });

    // BreadcrumbList schema
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: defaultSEO.url
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: agent.title.replace(' | Eagentix', ''),
          item: seo.url
        }
      ]
    });

    // FAQ schema if available
    if (agent.faqs && agent.faqs.length > 0) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: agent.faqs.map(faq => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer
          }
        }))
      });
    }

    // HowTo schema if available
    if (agent.howTo && agent.howTo.steps && agent.howTo.steps.length > 0) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: agent.howTo.name,
        description: agent.description,
        step: agent.howTo.steps.map(stepText => ({
          '@type': 'HowToStep',
          text: stepText
        }))
      });
    }
  } else {
    // Organization schema for homepage
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Eagentix',
      url: 'https://eagentix.com',
      logo: 'https://eagentix.com/logo512.png',
      description: 'Enterprise AI Agents for cloud, finance, data, sales, HR and interview automation. Model-agnostic platform with private deployment options.',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Singapore',
        postalCode: '079903',
        addressCountry: 'SG'
      },
      areaServed: 'Worldwide',
      knowsAbout: [
        'Enterprise AI Agents',
        'Agentic AI',
        'Cloud Architecture Automation',
        'Financial Accounting Automation',
        'Data Engineering Automation',
        'HR Recruitment Automation',
        'Interview Automation'
      ],
      sameAs: []
    });
  }

  return (
    <>
    <Helmet>
      <title>{seo.title}</title>
      <meta name="title" content={seo.title} />
      <meta name="description" content={seo.description} />

      <meta property="og:type" content="website" />
      <meta property="og:url" content={seo.url} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />

      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={seo.url} />
      <meta property="twitter:title" content={seo.title} />
      <meta property="twitter:description" content={seo.description} />
      <meta property="twitter:image" content={seo.image} />

      <link rel="canonical" href={seo.url} />

      {schemas.map((schema, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
    {artifactData && <ArtifactSEO {...artifactData} />}
    </>
  );
};

export default SEO;